import { WorkOrderChangeStatusEnum } from 'gql/graphql';
import { Button, Checkbox, IconExpand } from 'uikit';

import type { ICellRendererParams } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';

import type { TGroupedTableItem } from './WorkOrderChangeOtherPriceMenu';
import { WorkOrderChangeOtherPriceMenu } from './WorkOrderChangeOtherPriceMenu';

interface IMenuRendererProps extends ICellRendererParams<TGroupedTableItem> {
    checkedRows: Record<string, TGroupedTableItem | null> | null;
    checkRowHandler: (id: string, state: TGroupedTableItem | null) => void;
    openSingleDeleteModalHandler: () => void;
    openExcludeModalHandler: () => void;
    refreshTable: () => void;
    toggleTotalDetails: () => void;
    constructionTypeId: number | undefined;
    workOrderChangeStatus: WorkOrderChangeStatusEnum | undefined;
}

export const BimWorkOrderChangeMenuRenderer = (props: IMenuRendererProps) => {
    const {
        data,
        checkedRows,
        checkRowHandler,
        openSingleDeleteModalHandler,
        openExcludeModalHandler,
        constructionTypeId,
        refreshTable,
        toggleTotalDetails,
        node,
        workOrderChangeStatus,
    } = props;

    const disabledByWorkOrderChangeStatus = useMemo(
        () => workOrderChangeStatus !== WorkOrderChangeStatusEnum.OnFormation,
        [workOrderChangeStatus],
    );

    const isBottomTotalRow = useMemo(() => node.rowPinned === 'bottom', [node.rowPinned]);

    const currentRowId = useMemo(
        () => `${data?.workOrderOtherPriceId}_${data?.workOrderChangeOtherPriceId}`,
        [data?.workOrderChangeOtherPriceId, data?.workOrderOtherPriceId],
    );

    const isDeletionDisabled = useMemo(() => {
        if (disabledByWorkOrderChangeStatus || data?.isExcludedRow) {
            return true;
        }

        if (data?.isAddedRow) {
            return false;
        }

        return Boolean(!data?.isAddedRow && data?.hasActAcceptedCost !== null);
    }, [data?.hasActAcceptedCost, data?.isAddedRow, data?.isExcludedRow, disabledByWorkOrderChangeStatus]);

    const isChecked = useMemo(() => Boolean(checkedRows?.[currentRowId]) ?? false, [checkedRows, currentRowId]);

    const handleCheckbox = useCallback(
        (id: string) =>
            ({ checked }: { checked: boolean }) => {
                if (checked && data) {
                    checkRowHandler(id, data);
                } else {
                    checkRowHandler(id, null);
                }
            },
        [checkRowHandler, data],
    );

    const openSingleDeleteHandler = useCallback(() => {
        if (data) {
            checkRowHandler(currentRowId, data);
            openSingleDeleteModalHandler();
        }
    }, [checkRowHandler, currentRowId, data, openSingleDeleteModalHandler]);

    const openExcludeHandler = useCallback(() => {
        if (data) {
            checkRowHandler(currentRowId, data);
            openExcludeModalHandler();
        }
    }, [checkRowHandler, currentRowId, data, openExcludeModalHandler]);

    if (data?.isGroupTitle) {
        return null;
    }

    return !isBottomTotalRow ? (
        <>
            <Checkbox checked={isChecked} onChange={handleCheckbox(currentRowId)} disabled={isDeletionDisabled} />
            <WorkOrderChangeOtherPriceMenu
                data={data}
                openSingleDeleteHandler={openSingleDeleteHandler}
                openExcludeHandler={openExcludeHandler}
                isDeletionDisabled={isDeletionDisabled}
                constructionTypeId={constructionTypeId}
                refreshTable={refreshTable}
                disabledByWorkOrderChangeStatus={disabledByWorkOrderChangeStatus}
            />
        </>
    ) : (
        <Button onClick={toggleTotalDetails} iconLeft={IconExpand} onlyIcon view="clear" size="xs" />
    );
};

BimWorkOrderChangeMenuRenderer.displayName = 'BimWorkOrderChangeMenuRenderer';
